import type { User } from 'firebase/auth'
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore'
import type { MemberDoc, OrgDoc, UserDoc } from '@racedash/shared'
import { db } from '../lib/firebase'

/**
 * Ensures a signed-in user has a user document and a personal organisation.
 *
 * "Personal" is pure UX: structurally this is an ordinary org with one member,
 * so an individual who later wants team members never migrates anything — the
 * org flips from personal to shared and invites start working.
 *
 * Written client-side in a single batch rather than from an auth trigger, since
 * blocking functions need the Blaze plan and there is no other server tier yet.
 * The batch is atomic, and the whole function is idempotent, so a tab closed
 * mid-flight leaves nothing half-created.
 */
export async function ensureBootstrapped(user: User): Promise<string> {
  // Refused rather than trusted to the caller. An anonymous visitor holds an
  // identity only so the public share view can read timing; giving them an
  // organisation would be furniture nobody asked for and nothing ever removes.
  if (user.isAnonymous) {
    throw new Error('Anonymous visitors do not get an organisation.')
  }

  const userRef = doc(db, 'users', user.uid)
  const existing = await getDoc(userRef)

  const existingOrgId = existing.data()?.['defaultOrgId'] as string | undefined
  if (existingOrgId) return existingOrgId

  const orgRef = doc(collection(db, 'orgs'))

  const org: Omit<OrgDoc, 'createdAt'> = {
    // Apple returns displayName only on the very first sign-in and never again,
    // so whatever we have now is all we will ever get.
    name: user.displayName ? `${user.displayName}'s dashboards` : 'Personal',
    kind: 'personal',
    primaryOwnerUid: user.uid,
  }

  const member: Omit<MemberDoc, 'joinedAt'> = {
    uid: user.uid,
    role: 'owner',
    displayName: user.displayName,
    email: user.email,
  }

  const userDoc: Omit<UserDoc, 'createdAt'> = {
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL,
    defaultOrgId: orgRef.id,
  }

  const batch = writeBatch(db)
  batch.set(orgRef, { ...org, createdAt: serverTimestamp() })
  batch.set(doc(db, 'orgs', orgRef.id, 'members', user.uid), {
    ...member,
    joinedAt: serverTimestamp(),
  })
  batch.set(userRef, { ...userDoc, createdAt: serverTimestamp() }, { merge: true })
  await batch.commit()

  return orgRef.id
}
